import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Clock, Heart, Home, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeartBurst from "@/components/HeartBurst";
import ShareButtons from "@/components/ShareButtons";
import { formatBRL, getDonation, simulateDonation } from "@/lib/api";

export default function ThankYou() {
  const [params] = useSearchParams();
  const donationId = params.get("doacao");
  const [donation, setDonation] = useState(null);
  const [notFound, setNotFound] = useState(false);

  let embedded = params.get("embed") === "1";
  if (!embedded) {
    try {
      embedded = window.self !== window.top;
    } catch (e) {
      embedded = true;
    }
  }

  useEffect(() => {
    if (!donationId) {
      setNotFound(true);
      return;
    }
    let alive = true;
    const load = async () => {
      try {
        const d = await getDonation(donationId);
        if (alive) setDonation(d);
        return d;
      } catch (e) {
        if (alive) setNotFound(true);
        return null;
      }
    };
    load();
    const iv = setInterval(async () => {
      const d = await load();
      if (d && d.status === "received") clearInterval(iv);
    }, 5000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [donationId]);

  const simulate = async () => {
    try {
      const d = await simulateDonation(donationId);
      setDonation(d);
    } catch (e) {
      /* noop */
    }
  };

  const closeParent = () => {
    try {
      window.parent.postMessage({ type: "close-pix-modal" }, "*");
    } catch (e) {
      /* noop */
    }
  };

  const received = donation?.status === "received";

  /* ---------- Modo embutido (dentro do iframe do checkout) ---------- */
  if (embedded) {
    return (
      <div className="grid min-h-screen place-items-center bg-white p-4" data-testid="thankyou-embedded">
        {notFound && (
          <div className="text-center">
            <Heart className="mx-auto h-10 w-10 text-[#FF6D47]" />
            <h1 className="font-display mt-4 text-xl font-extrabold text-[#261C14]">Doação não encontrada</h1>
            <p className="mt-2 text-sm font-semibold text-[#6E6259]">Você já pode fechar esta janela.</p>
          </div>
        )}
        {!notFound && !donation && <Loader2 className="h-8 w-8 animate-spin text-[#FF6D47]" />}
        {!notFound && donation && !received && (
          <div className="text-center" data-testid="thankyou-embedded-pending">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#FF6D47]" />
            <h1 className="font-display mt-4 text-xl font-extrabold text-[#261C14]">Aguardando o seu Pix...</h1>
            <p className="font-display mt-1 text-3xl font-extrabold text-[#FF6D47]">{formatBRL(donation.amount_cents)}</p>
            <p className="mt-3 text-xs font-semibold text-[#6E6259]">Esta página atualiza sozinha quando o pagamento cair.</p>
          </div>
        )}
        {!notFound && donation && received && (
          <div className="relative w-full text-center">
            <HeartBurst />
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 16 }}>
              <CheckCircle2 className="mx-auto h-14 w-14 text-[#059669]" />
            </motion.div>
            <h1 data-testid="thankyou-embedded-success" className="font-display mt-3 text-2xl font-extrabold text-[#261C14]">
              Doação confirmada!
            </h1>
            <p className="mt-2 text-sm font-semibold text-[#6E6259]">
              Obrigado, <span className="font-extrabold text-[#261C14]">{donation.donor_name}</span>! Sua doação de{" "}
              <span className="font-extrabold text-[#FF6D47]">{formatBRL(donation.amount_cents)}</span> já virou ração e
              carinho para as patinhas do abrigo.
            </p>
            <button
              onClick={closeParent}
              data-testid="thankyou-embedded-close-btn"
              className="mt-5 h-11 w-full rounded-full bg-[#FF6D47] text-sm font-extrabold text-white transition hover:bg-[#E85732]"
            >
              Fechar
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ---------- Página completa ---------- */
  return (
    <div className="min-h-screen">
      <Header />
      <main className="grid min-h-[70vh] place-items-center px-4 py-14" data-testid="thankyou-page">
        {notFound && (
          <div className="max-w-md text-center">
            <Heart className="mx-auto h-14 w-14 text-[#FF6D47]" />
            <h1 className="font-display mt-6 text-3xl font-extrabold text-[#261C14]">Doação não encontrada</h1>
            <p className="mt-3 font-semibold text-[#6E6259]">Que tal conhecer as causas do abrigo e fazer a sua doação?</p>
            <Link
              to="/"
              data-testid="thankyou-back-home-btn"
              className="mt-8 inline-flex h-14 items-center gap-2 rounded-full bg-[#FF6D47] px-8 font-extrabold text-white transition hover:bg-[#E85732]"
            >
              <Home className="h-4 w-4" /> Voltar para a home
            </Link>
          </div>
        )}

        {!notFound && !donation && <Loader2 className="h-10 w-10 animate-spin text-[#FF6D47]" />}

        {!notFound && donation && !received && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="thankyou-pending"
            className="w-full max-w-md rounded-[1.75rem] border border-[#EFE9DF] bg-white p-8 text-center shadow-xl shadow-[#FF6D47]/5"
          >
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#FFF0EB]">
              <Loader2 className="h-8 w-8 animate-spin text-[#FF6D47]" />
            </span>
            <h1 className="font-display mt-6 text-2xl font-extrabold text-[#261C14]">Aguardando o seu Pix...</h1>
            <p className="font-display mt-2 text-4xl font-extrabold text-[#FF6D47]">{formatBRL(donation.amount_cents)}</p>
            <p className="mt-4 text-sm font-semibold text-[#6E6259]">
              Esta página atualiza sozinha. Se você ainda não pagou, abra o app do seu banco e escaneie o QR Code do
              checkout LivePix.
            </p>
            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs font-bold text-[#9E9085]">
              <Clock className="h-3.5 w-3.5" /> Assim que o pagamento cair, a mágica acontece por aqui.
            </p>
            {donation.demo && (
              <button
                onClick={simulate}
                data-testid="thankyou-simulate-btn"
                className="mt-6 h-12 w-full rounded-full bg-[#059669] text-sm font-extrabold text-white transition hover:bg-[#047857]"
              >
                Simular confirmação do Pix (modo teste)
              </button>
            )}
          </motion.div>
        )}

        {!notFound && donation && received && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative w-full max-w-md rounded-[1.75rem] border border-[#FFE2D9] bg-white p-8 text-center shadow-xl shadow-[#FF6D47]/10"
          >
            <HeartBurst />
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 16 }}>
              <CheckCircle2 className="mx-auto h-16 w-16 text-[#059669]" />
            </motion.div>
            <h1 data-testid="thankyou-success-message" className="font-display mt-4 text-3xl font-extrabold text-[#261C14]">
              Doação confirmada!
            </h1>
            <p className="mt-3 text-sm font-semibold text-[#6E6259]">
              Obrigado, <span className="font-extrabold text-[#261C14]">{donation.donor_name}</span>! Sua doação de{" "}
              <span className="font-extrabold text-[#FF6D47]">{formatBRL(donation.amount_cents)}</span> já virou ração,
              vacina e carinho para as patinhas do abrigo.
            </p>

            <div className="mt-8 rounded-2xl bg-[#FFF0EB] p-5 text-left">
              <p className="font-display font-extrabold text-[#261C14]">Chame mais gente para ajudar!</p>
              <p className="mt-1 text-sm text-[#6E6259]">
                Cada compartilhamento multiplica as chances de mais uma segunda chance.
              </p>
            </div>

            <ShareButtons title="Acabei de doar para o Instituto PetLovers — doe você também!" className="mt-4" />

            <div className="mt-6 flex flex-col gap-3">
              <Link
                to="/vaquinha/ajude-o-instituto-petlovers-a-cuidar-de-centenas-de-patinhas"
                data-testid="thankyou-campaign-link"
                className="flex h-12 items-center justify-center gap-2 rounded-full bg-[#FF6D47] font-extrabold text-white transition hover:bg-[#E85732]"
              >
                Voltar para a campanha <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/"
                data-testid="thankyou-home-link"
                className="flex h-12 items-center justify-center gap-2 rounded-full border-2 border-[#EFE9DF] bg-white font-extrabold text-[#261C14] transition hover:border-[#FF6D47] hover:text-[#FF6D47]"
              >
                <Home className="h-4 w-4" /> Ir para a home
              </Link>
            </div>
          </motion.div>
        )}
      </main>
      <Footer />
    </div>
  );
}
